export interface ReportFileRef {
  s3Key?: string;
  docRef?: number;
  fileName?: string;
  fileUrl: string;
  projectId?: number;
  reportType?: string;
  spreadId?: string | null;
  subSpreadId?: string | null;
  uploadedBy?: string | null;
}

function normalizeFileRefItem(raw: unknown): ReportFileRef | null {
  if (!raw || typeof raw !== 'object') return null;

  const row = raw as Record<string, unknown>;
  const fileUrl = String(row.fileUrl ?? row.file_url ?? '').trim();
  if (!fileUrl) return null;

  const docRefRaw = row.docRef ?? row.doc_ref;
  const projectIdRaw = row.projectId ?? row.project_id;

  return {
    s3Key:
      row.s3Key != null
        ? String(row.s3Key)
        : row.s3_key != null
          ? String(row.s3_key)
          : undefined,
    docRef: docRefRaw != null && docRefRaw !== '' ? Number(docRefRaw) : undefined,
    fileName:
      row.fileName != null
        ? String(row.fileName)
        : row.file_name != null
          ? String(row.file_name)
          : undefined,
    fileUrl,
    projectId:
      projectIdRaw != null && projectIdRaw !== '' ? Number(projectIdRaw) : undefined,
    reportType:
      row.reportType != null
        ? String(row.reportType)
        : row.report_type != null
          ? String(row.report_type)
          : undefined,
    spreadId:
      row.spreadId != null
        ? String(row.spreadId)
        : row.spread_id != null
          ? String(row.spread_id)
          : null,
    subSpreadId:
      row.subSpreadId != null
        ? String(row.subSpreadId)
        : row.sub_spread_id != null
          ? String(row.sub_spread_id)
          : null,
    uploadedBy:
      row.uploadedBy != null
        ? String(row.uploadedBy)
        : row.uploaded_by != null
          ? String(row.uploaded_by)
          : null,
  };
}

/** Parse report-history / report-versions fileUrl as a string or file-ref array. */
export function parseReportFileRefs(raw: unknown): ReportFileRef[] {
  if (raw == null || raw === '') return [];

  if (typeof raw === 'string') {
    const fileUrl = raw.trim();
    return fileUrl ? [{ fileUrl }] : [];
  }

  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeFileRefItem(item))
      .filter((item): item is ReportFileRef => item != null);
  }

  const single = normalizeFileRefItem(raw);
  return single ? [single] : [];
}

export function pickReportFileRefByType(
  raw: unknown,
  reportType?: string
): ReportFileRef | undefined {
  const refs = parseReportFileRefs(raw);
  if (!refs.length) return undefined;

  if (reportType) {
    const match = refs.find((ref) => ref.reportType === reportType && ref.fileUrl?.trim());
    if (match) return match;
  }

  return refs.find((ref) => ref.fileUrl?.trim());
}

export function pickPrimaryReportFileUrl(raw: unknown): string {
  return pickReportFileRefByType(raw)?.fileUrl ?? '';
}

export function pickPrimaryReportFileName(raw: unknown): string | undefined {
  return pickReportFileRefByType(raw)?.fileName;
}

export function pickReportFileUrlByType(raw: unknown, reportType?: string): string {
  return pickReportFileRefByType(raw, reportType)?.fileUrl ?? '';
}

export function pickReportFileNameByType(
  raw: unknown,
  reportType?: string
): string | undefined {
  return pickReportFileRefByType(raw, reportType)?.fileName;
}
